import { Theme } from "@mui/material";
import { toDisplayStatusColor } from "components/common/Utils/commonUtilityFunctions";
import { WorkOrderStatus } from "../../../models/lookupTypes";
import { WorkOrder } from "../models/workOrderTypes";

export type WoStatusWithColors = WorkOrderStatus & {
  BackColor?: string;
  ForeColor?: string;
};

export interface WorkOrderStatusColors {
  bgcolor: string;
  color: string;
  borderColor: string;
  chipSx: Record<string, unknown>;
}

const isValidColor = (color: string | null | undefined): boolean =>
  !!(
    color &&
    color.trim() !== "" &&
    color !== "transparent" &&
    color !== "null"
  );

// Dynamic fore color based on background luminance — reserved for future use.
// interface ParsedStatusColor {
//   r: number;
//   g: number;
//   b: number;
//   a: number;
// }
//
// const blendChannel = (
//   foreground: number,
//   background: number,
//   alpha: number,
// ): number => Math.round(foreground * alpha + background * (1 - alpha));
//
// const parseStatusColor = (color: string): ParsedStatusColor | null => {
//   const trimmed = color.trim();
//
//   if (/^#[0-9A-Fa-f]{8}$/.test(trimmed)) {
//     return {
//       a: parseInt(trimmed.slice(1, 3), 16) / 255,
//       r: parseInt(trimmed.slice(3, 5), 16),
//       g: parseInt(trimmed.slice(5, 7), 16),
//       b: parseInt(trimmed.slice(7, 9), 16),
//     };
//   }
//
//   if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
//     return {
//       a: 1,
//       r: parseInt(trimmed.slice(1, 3), 16),
//       g: parseInt(trimmed.slice(3, 5), 16),
//       b: parseInt(trimmed.slice(5, 7), 16),
//     };
//   }
//
//   const rgbaMatch = trimmed.match(
//     /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/,
//   );
//   if (rgbaMatch) {
//     return {
//       r: Number(rgbaMatch[1]),
//       g: Number(rgbaMatch[2]),
//       b: Number(rgbaMatch[3]),
//       a: rgbaMatch[4] !== undefined ? Number(rgbaMatch[4]) : 1,
//     };
//   }
//
//   return null;
// };
//
// const toHexColor = ({ r, g, b }: Pick<ParsedStatusColor, "r" | "g" | "b">) =>
//   `#${[r, g, b]
//     .map((channel) =>
//       Math.max(0, Math.min(255, channel)).toString(16).padStart(2, "0"),
//     )
//     .join("")}`;
//
// /** Picks black or white label text based on the effective status background color. */
// export const getReadableStatusTextColor = (
//   backgroundColor: string,
//   theme: Theme,
// ): string => {
//   const parsedBackground = parseStatusColor(backgroundColor);
//   if (!parsedBackground) {
//     return theme.palette.mode === "light" ? "#212121" : "#ffffff";
//   }
//
//   const surfaceColor =
//     theme.palette.mode === "light" ? "#ffffff" : theme.palette.background.paper;
//   const parsedSurface =
//     parseStatusColor(surfaceColor) ?? { r: 255, g: 255, b: 255, a: 1 };
//
//   const effectiveColor = toHexColor({
//     r: blendChannel(parsedBackground.r, parsedSurface.r, parsedBackground.a),
//     g: blendChannel(parsedBackground.g, parsedSurface.g, parsedBackground.a),
//     b: blendChannel(parsedBackground.b, parsedSurface.b, parsedBackground.a),
//   });
//
//   return theme.palette.getContrastText(effectiveColor);
// };

export const resolveWorkOrderStatusId = (
  workOrder: WorkOrder,
): number | undefined => {
  const statusId =
    workOrder.workOrderStatusIdentity ??
    workOrder.woStatusIdentity ??
    workOrder.WorkOrderStatusIdentity;

  return statusId != null && statusId > 0 ? statusId : undefined;
};

export const findWorkOrderStatus = (
  workOrder: WorkOrder,
  woStatuses: WoStatusWithColors[],
): WoStatusWithColors | undefined => {
  const statusId = resolveWorkOrderStatusId(workOrder);
  if (statusId) {
    const byId = woStatuses.find((status) => status.identity === statusId);
    if (byId) {
      return byId;
    }
  }

  const statusLabel = workOrder.status?.trim();
  if (!statusLabel) {
    return undefined;
  }

  return woStatuses.find(
    (status) =>
      status.displayText?.trim().toLowerCase() === statusLabel.toLowerCase(),
  );
};

export const getWorkOrderStatusColors = (
  workOrder: WorkOrder,
  woStatuses: WoStatusWithColors[],
  theme: Theme,
): WorkOrderStatusColors => {
  const status = findWorkOrderStatus(workOrder, woStatuses);
  const cachedBgColor =
    status?.backColor || status?.BackColor || status?.color;
  const cachedTextColor = status?.foreColor || status?.ForeColor;

  const defaultBgColor =
    theme.palette.mode === "light" ? "#FFE0E0E0" : "#FF424242";
  const defaultTextColor =
    theme.palette.mode === "light" ? "#FF424242" : "#FFE0E0E0";

  const bgcolor = isValidColor(cachedBgColor) ? cachedBgColor! : defaultBgColor;
  const color = isValidColor(cachedTextColor)
    ? cachedTextColor!
    : defaultTextColor;
  const borderColor = toDisplayStatusColor(bgcolor);
  // const color = getReadableStatusTextColor(bgcolor, theme);

  return {
    bgcolor,
    color,
    borderColor,
    chipSx: {
      minWidth: "91px",
      marginRight: "10px",
      borderRadius: "5px",
      fontWeight: 600,
      fontSize: "12px",
      fontStyle: "normal",
      height: "auto",
      py: 0.25,
      backgroundColor: borderColor,
      color,
      border: `1px solid ${borderColor}`,
      borderTop: `3px solid ${borderColor}`,
      boxShadow: "none",
      "& .MuiChip-label": {
        px: 1,
      },
    },
  };
};

export const woStatusesHaveColors = (
  woStatuses: WoStatusWithColors[],
): boolean =>
  woStatuses.some(
    (status) =>
      isValidColor(status.backColor) ||
      isValidColor(status.BackColor) ||
      isValidColor(status.color),
  );
